<template>
  <v-content>
    <v-container>
      <v-layout justify-center class="mt-4 mb-4">
        <v-flex xs12 sm10 md7>
          <v-form
            ref="form"
            lazy-validation
            @submit.prevent="submit"
            @click.native.capture="closeStuckSelectMenus"
          >
            <h2 class="headline pb-2">Request a Provider Account</h2>
            <p class="body-1 pb-3">
              Fill out the form below to request access. An administrator will
              review your submission and reach out if approved.
            </p>

            <v-layout wrap>
              <v-flex xs12 sm6 style="padding-right: 8px">
                <v-text-field
                  v-model="form.first_name"
                  label="First Name"
                  solo
                  flat
                  :rules="[required]"
                  :disabled="loading"
                />
              </v-flex>
              <v-flex xs12 sm6 style="padding-left: 8px">
                <v-text-field
                  v-model="form.last_name"
                  label="Last Name"
                  solo
                  flat
                  :rules="[required]"
                  :disabled="loading"
                />
              </v-flex>
            </v-layout>

            <v-text-field
              v-model="form.email"
              label="Email Address"
              solo
              flat
              type="email"
              :rules="[required, validEmail]"
              :error-messages="emailError"
              :disabled="loading"
              @input="emailError = ''"
            />

            <v-text-field
              v-model="form.password"
              label="Password"
              solo
              flat
              type="password"
              :rules="[required, minLength]"
              hint="Minimum 8 characters"
              persistent-hint
              :disabled="loading"
            />

            <v-text-field
              v-model="form.organization"
              label="Organization"
              solo
              flat
              :rules="[required]"
              :disabled="loading"
            />

            <v-text-field
              v-model="form.phone"
              label="Phone Number"
              solo
              flat
              :disabled="loading"
            />

            <v-select
              ref="servicesSelect"
              v-model="form.requested_services"
              :items="services"
              item-text="title"
              item-value="title"
              label="Service(s) you want to create listings for"
              solo
              flat
              multiple
              chips
              small-chips
              :rules="[atLeastOne]"
              :disabled="loading"
              :loading="loadingServices"
              no-data-text="No services available — please refresh the page"
            />

            <v-textarea
              v-model="form.reason"
              label="Why are you requesting an account?"
              solo
              flat
              rows="4"
              :rules="[required, minReason]"
              :disabled="loading"
            />

            <div class="mb-3">
              <div class="caption mb-1">
                Type the characters shown below
              </div>
              <v-layout align-center>
                <div class="captcha-image mr-2" v-html="captchaSvg" />
                <v-btn
                  icon
                  small
                  :loading="captchaLoading"
                  @click="loadCaptcha"
                  title="Refresh captcha"
                >
                  <v-icon>refresh</v-icon>
                </v-btn>
              </v-layout>
              <v-text-field
                v-model="captchaAnswer"
                label="Enter characters above"
                solo
                flat
                :rules="[required]"
                :disabled="loading"
                class="mt-2"
              />
            </div>

            <v-alert v-if="errorMessage" type="error" class="mb-3">
              {{ errorMessage }}
            </v-alert>

            <v-layout align-center>
              <v-btn
                depressed
                color="secondary"
                class="white--text ma-0"
                type="submit"
                :loading="loading"
              >
                Submit Request
              </v-btn>
              <span class="ml-3 body-2">
                Already have an account?
                <router-link :to="{ name: 'Provider-Login' }"
                  >Log in</router-link
                >
              </span>
            </v-layout>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>
    <v-dialog v-model="successDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">Submission Successful</v-card-title>
        <v-card-text>
          You will receive an email once an admin has reviewed your submission.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            class="white--text"
            depressed
            @click="confirmSuccess"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-content>
</template>

<script>
import axios from "axios";

const baseUrl =
  (window.location.hostname === "localhost"
    ? "http://" + window.location.hostname + ":3000"
    : window.location.origin) + "/api";

export default {
  name: "ProviderRegister",
  data() {
    return {
      form: {
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        organization: "",
        phone: "",
        reason: "",
        requested_services: []
      },
      captchaSvg: "",
      captchaToken: "",
      captchaAnswer: "",
      captchaLoading: false,
      loading: false,
      loadingServices: false,
      services: [],
      errorMessage: "",
      emailError: "",
      successDialog: false
    };
  },
  mounted() {
    this.loadCaptcha();
    this.fetchServices();
  },
  methods: {
    required: v => !!v || "This field is required.",
    validEmail: v =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email address.",
    minLength: v =>
      (v && v.length >= 8) || "Password must be at least 8 characters.",
    minReason: v =>
      (v && v.length >= 10) ||
      "Please provide more detail (at least 10 characters).",
    atLeastOne: v =>
      (Array.isArray(v) && v.length > 0) || "Select at least one service.",

    // Vuetify 1.5's v-select only listens for outside clicks while its input
    // is focused. Clicking straight from an open multi-select into another
    // field blurs it without closing the menu, leaving it stuck open and
    // covering the rest of the form. Force it closed here instead, unless
    // the click actually landed on the select or its (portal-rendered) menu.
    closeStuckSelectMenus(e) {
      const openSelects = [this.$refs.servicesSelect].filter(
        sel => sel && sel.isMenuActive
      );
      openSelects.forEach(sel => {
        const menuContent = sel.$refs.menu && sel.$refs.menu.$refs.content;
        const clickedSelect = sel.$el.contains(e.target);
        const clickedMenu = menuContent && menuContent.contains(e.target);
        if (!clickedSelect && !clickedMenu) {
          sel.blur();
        }
      });
    },

    async loadCaptcha() {
      this.captchaLoading = true;
      this.captchaAnswer = "";
      try {
        const res = await axios.get(`${baseUrl}/providers/captcha`);
        this.captchaSvg = res.data.svg;
        this.captchaToken = res.data.token;
      } catch {
        this.errorMessage = "Could not load CAPTCHA. Please refresh the page.";
      } finally {
        this.captchaLoading = false;
      }
    },

    confirmSuccess() {
      this.successDialog = false;
      this.$router.push({ name: "Categories" });
    },

    async fetchServices() {
      this.loadingServices = true;
      try {
        const res = await axios.get(`${baseUrl}/services`);
        this.services = res.data.data || [];
      } catch (err) {
        console.error("Failed to load services:", err);
        this.errorMessage =
          "Could not load the services list. Please refresh the page.";
      } finally {
        this.loadingServices = false;
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.errorMessage = "";
      this.emailError = "";
      this.loading = true;

      try {
        await this.$store.dispatch("registerProvider", {
          ...this.form,
          captchaToken: this.captchaToken,
          captchaAnswer: this.captchaAnswer
        });
        this.successDialog = true;
      } catch (err) {
        const msg = err.message || "Submission failed. Please try again.";
        if (msg === "This email is already registered.") {
          this.emailError = msg;
        } else {
          this.errorMessage = msg;
        }
        this.loadCaptcha();
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
form {
  padding: 2em;
  border: 1px solid #ccc;
  border-radius: 0.5em;
}

.captcha-image {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  display: inline-block;
  line-height: 0;
}
</style>
